import {
  getCommercialProgram,
  getProgramCommitments,
} from 'common/src/api/commercialProgram';
import { getShipmentsDetailsPage } from 'common/src/api/shipments';
import {
  commitmentsAt,
  convertQuantity,
  weekOfDate,
} from 'common/src/utils/commercialProgram';
import {
  getSeasonCutoffIndex,
  getSeasonWeeksForSpecies,
} from 'common/src/utils/seasonWeeks';
import type { ProgramUnit } from 'common/src/utils/types';
import {
  buildRecentShipments,
  buildShipmentRows,
  buildShipmentsToDate,
  buildUpcomingArrivals,
  filterBoxesByPeriod,
  listDepartureWeeks,
  reportCutoffWeek,
  type ReportShipmentBox,
} from '../utils/client-reports/build-shipments-section';
import {
  buildOverdueSettlements,
  buildPendingSettlementsSummary,
  buildSettlementsByVessel,
  buildSettlementsDueSoon,
} from '../utils/client-reports/build-pending-settlements-section';
import {
  buildCommercialPlanSection,
  restOfClients,
  type ReportCommitment,
  type ReportProgram,
} from '../utils/client-reports/build-commercial-plan-section';
import type {
  ClientReportKind,
  ClientReportSection,
  ClientReportSnapshot,
  ReportPeriod,
  ReportSectionType,
} from '../utils/client-reports/types';

export type ClientReportData = {
  boxes: ReportShipmentBox[];
  // Referencias con liquidación aprobada, recortadas igual que en Despachos.
  settledReferences: Set<string>;
  program: ReportProgram | null;
  commitments: ReportCommitment[];
};

// Datos de la temporada para armar el reporte:
// - Despachos con su tracking y si están liquidados, de la misma fuente que la
//   pantalla de Despachos: nuestra base, también para las exportadoras con
//   Frutic (sus embarques se importan y se leen desde ahí).
// - Programa Comercial: los compromisos vigentes, en la unidad del programa.
// El cliente queda con el nombre que se ve en Despachos.
export const loadClientReportData = async ({
  exporterId,
  season,
  speciesId,
  receiverNameById,
}: {
  exporterId: number;
  season: string;
  speciesId: number;
  receiverNameById: Map<number, string>;
}): Promise<ClientReportData> => {
  const [{ boxes, settledReferences }, program] = await Promise.all([
    loadReportShipmentBoxes({
      exporterId,
      season,
      speciesId,
      receiverNameById,
    }),
    getCommercialProgram(exporterId, season, speciesId),
  ]);
  if (!program) {
    return { boxes, settledReferences, program: null, commitments: [] };
  }

  const rows = commitmentsAt(await getProgramCommitments(program.id));
  return {
    boxes,
    settledReferences,
    program: {
      unit: program.unit,
      date_basis: program.date_basis,
      kg_per_ceq: Number(program.kg_per_ceq),
    },
    commitments: rows.map((row) => ({
      receiver: receiverNameById.get(Number(row.exporter_receiver_id)) || '',
      week: Number(row.week),
      quantity:
        convertQuantity(
          Number(row.quantity) || 0,
          (row.unit as ProgramUnit) || program.unit,
          program.unit,
          program
        ) || 0,
    })),
  };
};

const loadReportShipmentBoxes = async ({
  exporterId,
  season,
  speciesId,
  receiverNameById,
}: {
  exporterId: number;
  season: string;
  speciesId: number;
  receiverNameById: Map<number, string>;
}): Promise<{ boxes: ReportShipmentBox[]; settledReferences: Set<string> }> => {
  const payload = await getShipmentsDetailsPage(exporterId, season, speciesId);
  if (!payload) throw new Error('No shipment details payload returned');
  return {
    boxes: payload.rows.map((row) => {
      const box = row as unknown as ReportShipmentBox & {
        exporter_receiver_id?: number | null;
      };
      return {
        ...box,
        receiver:
          receiverNameById.get(Number(box.exporter_receiver_id)) ||
          box.receiver ||
          '',
      };
    }),
    // El RPC ya cruza cada despacho con sus liquidaciones aprobadas.
    settledReferences: new Set(
      payload.rows
        .filter((row) => row.status === 'settled')
        .map((row) => row.reference_number?.trim())
        .filter((reference): reference is string => Boolean(reference))
    ),
  };
};

export const buildClientReportSnapshot = ({
  kind,
  data,
  receivers,
  period,
  sections,
  exporterName,
  exporterLogoUrl,
  speciesId,
  speciesName,
  season,
  kgPerCeq,
  generatedAt,
  recentWeekStart = null,
  caliberOrder = null,
  compareWithRest = false,
}: {
  kind: ClientReportKind;
  data: ClientReportData;
  receivers: string[];
  period: ReportPeriod;
  sections: ReportSectionType[];
  exporterName: string;
  exporterLogoUrl: string | null;
  speciesId: number;
  speciesName: string;
  season: string;
  kgPerCeq: number;
  generatedAt: Date;
  // Lunes de la semana de corte; sin valor, la última semana completa con
  // zarpes.
  recentWeekStart?: string | null;
  // Calibres de la especie, de menor a mayor; sin valor, el orden por defecto.
  caliberOrder?: string[] | null;
  // Mostrar junto al cumplimiento del plan el del resto de los clientes.
  compareWithRest?: boolean;
}): ClientReportSnapshot => {
  const receiverSet = new Set(receivers);
  const seasonBoxes = data.boxes.filter((box) =>
    receiverSet.has(box.receiver || '')
  );
  const receiverBoxes = filterBoxesByPeriod(seasonBoxes, period);
  const weeks = getSeasonWeeksForSpecies(speciesId, season);

  const rows = buildShipmentRows({
    boxes: receiverBoxes,
    kgPerCeq,
    today: generatedAt,
  });
  // La semana de corte manda en el acumulado, el cumplimiento a la fecha y
  // los últimos embarques. Los próximos arribos se miden desde hoy.
  const cutoff = reportCutoffWeek({
    rows,
    today: generatedAt,
    weekOf: weekOfDate,
    recentWeekStart,
  });
  const input = {
    rows,
    today: generatedAt,
    weekOf: weekOfDate,
    recentWeekStart: cutoff?.start ?? null,
  };
  const rowsToCutoff = cutoff
    ? rows.filter((row) => !row.etd || row.etd <= cutoff.end)
    : rows;
  const cutoffIndex = getSeasonCutoffIndex({
    weeks,
    selectedSeason: season,
    now: cutoff ? new Date(`${cutoff.end}T12:00:00`) : generatedAt,
  });
  const settlementsInput = {
    rows,
    settledReferences: data.settledReferences,
    today: generatedAt,
  };

  const buildSection = (type: ReportSectionType): ClientReportSection => {
    switch (type) {
      case 'shipmentsToDate': {
        // El plan es de la temporada completa: se compara contra todo lo
        // despachado de la temporada.
        const planFor = (
          commitments: ReportCommitment[],
          boxes: ReportShipmentBox[]
        ) =>
          buildCommercialPlanSection({
            program: data.program,
            commitments,
            rows: buildShipmentRows({
              boxes,
              kgPerCeq,
              today: generatedAt,
              includeNotDeparted: true,
            }),
            weeks,
            cutoffIndex,
            weekOf: weekOfDate,
          });
        const plan = planFor(
          data.commitments.filter((c) => receiverSet.has(c.receiver)),
          seasonBoxes
        );
        const rest = compareWithRest
          ? restOfClients({
              commitments: data.commitments,
              boxes: data.boxes,
              receivers: receiverSet,
            })
          : null;
        const restPlan =
          plan.hasPlan && rest?.clients ? planFor(rest.commitments, rest.boxes) : null;
        return {
          type,
          version: 1,
          data: buildShipmentsToDate({
            ...input,
            rows: rowsToCutoff,
            kgPerCeq,
            caliberOrder: caliberOrder?.length
              ? caliberOrder.map((caliber) => caliber.trim().toUpperCase())
              : undefined,
            // 2 es cereza.
            twoJUp: speciesId === 2,
            plan,
            restOfClients: restPlan?.hasPlan
              ? { ...restPlan.totals, clients: rest!.clients }
              : undefined,
          }),
        };
      }
      case 'upcomingArrivals':
        return { type, version: 1, data: buildUpcomingArrivals(input) };
      case 'recentShipments':
        return { type, version: 1, data: buildRecentShipments(input) };
      case 'pendingSettlementsSummary':
        return {
          type,
          version: 1,
          data: buildPendingSettlementsSummary(settlementsInput),
        };
      case 'settlementsByVessel':
        return { type, version: 1, data: buildSettlementsByVessel(settlementsInput) };
      case 'overdueSettlements':
        return { type, version: 1, data: buildOverdueSettlements(settlementsInput) };
      case 'settlementsDueSoon':
        return { type, version: 1, data: buildSettlementsDueSoon(settlementsInput) };
    }
  };

  return {
    version: 1,
    kind,
    generatedAt: generatedAt.toISOString(),
    exporterName,
    exporterLogoUrl,
    speciesId,
    speciesName,
    season,
    period,
    // El corte es del informe de despachos; el de liquidaciones se mide a hoy.
    ...(cutoff && kind === 'shipments'
      ? { cutoff: { date: cutoff.end, week: cutoff.week } }
      : {}),
    receivers,
    sections: sections.map(buildSection),
  };
};

// Semanas con zarpes de los clientes elegidos, para el selector de la semana
// de últimos embarques.
export const listReportDepartureWeeks = ({
  data,
  receivers,
  kgPerCeq,
  today,
}: {
  data: ClientReportData;
  receivers: string[];
  kgPerCeq: number;
  today: Date;
}) => {
  const receiverSet = new Set(receivers);
  const rows = buildShipmentRows({
    boxes: data.boxes.filter((box) => receiverSet.has(box.receiver || '')),
    kgPerCeq,
    today,
  });
  return listDepartureWeeks({ rows, today, weekOf: weekOfDate });
};
